import {
  ResponsiveObj,
  ResponsiveProp,
  UiBaseProps,
} from "../../utils/types/ui";
import { defaultUiConfig } from "../../utils/types/uiConfig";

// Type Guard
function isResponsiveObject<T>(
  val: ResponsiveProp<T>,
): val is ResponsiveObj<T> {
  return typeof val === "object" && val !== null && !Array.isArray(val);
}

// Utility: normalize to responsive array [mob, tab, web]
const getResponsive = <T>(
  val: ResponsiveProp<T> | undefined,
  defaultObj: ResponsiveObj<T>,
): T[] => {
  if (Array.isArray(val)) {
    // already responsive array
    return val;
  }

  if (isResponsiveObject(val)) {
    // object form { mob, tab, web }
    return [
      val.mob ?? defaultObj.mob!,
      val.tab ?? val.mob ?? defaultObj.tab!,
      val.web ?? val.tab ?? val.mob ?? defaultObj.web!,
    ];
  }

  if (val !== undefined) {
    // single value override: apply to all breakpoints
    return [val as T, val as T, val as T];
  }

  // fallback to default responsive object
  return [defaultObj.mob!, defaultObj.tab!, defaultObj.web!];
};

// 🔹 Hook
export function useResponsiveUi(props: UiBaseProps = {}) {
  const config = { ...defaultUiConfig, ...props };

  return {
    size: getResponsive(config.size, defaultUiConfig.size),
    fontSize: getResponsive(config.fontSize, defaultUiConfig.fontSize),
    rounded: getResponsive(config.rounded, defaultUiConfig.rounded),
    shadow: getResponsive(config.shadow, defaultUiConfig.shadow),
    shadowColor: getResponsive(config.shadowColor, defaultUiConfig.shadowColor),
    padding: getResponsive(config.padding, defaultUiConfig.padding),
    margin: getResponsive(config.margin, defaultUiConfig.margin),
    variant: config.variant ?? defaultUiConfig.variant,
    colorPalette: config.colorPalette ?? defaultUiConfig.colorPalette,
    borderWidth: config.borderWidth ?? defaultUiConfig.borderWidth,
    lineHeight: config.lineHeight ?? defaultUiConfig.lineHeight,
    letterSpacing: config.letterSpacing ?? defaultUiConfig.letterSpacing,
    transition: config.transition ?? defaultUiConfig.transition,
  };
}
